import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { WorkSessionSummaryDTO, WorkerDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { DataTable, type Column } from "@/design/DataTable";
import { cs } from "@/i18n/cs";
import { Btn, EmptyState, Field, PageHeader, Panel, inputClass, toneColor, type Tone } from "@/design/primitives";
import { RoutinesPanel } from "./RoutinesPanel";
import { relativeTime, sessionCostLabel, sessionStepsLabel, untilTime } from "@/lib/format";
import { useAuth } from "@/auth/auth";
import { useRealtimeEvent } from "@/realtime/useRealtime";

/**
 * Limity předplatného stroje (co ukazuje `/usage` v Claude Code). Runner je
 * hlásí z `rate_limit_event`, takže jde vidět, který stroj je u stropu dřív,
 * než začne odmítat práci. Starý údaj se schová — po pár hodinách je bezcenný
 * a vypadal by jako čerstvý.
 */
const RATE_LIMIT_STALE_MS = 3 * 60 * 60 * 1000;

/** Zdroj hlásí vyčerpaná procenta; na kartě má být, co ZBÝVÁ. */
function remaining(usedPct: number | null): number | null {
  return usedPct == null ? null : Math.max(0, Math.round(100 - usedPct));
}

/**
 * Semafor podle zbývající kapacity okna. Vyčerpané okno se nekreslí jen
 * červeně, ale plnou výplní — na kartě mezi ostatními chipy musí praštit
 * do očí, protože znamená, že stroj stojí.
 */
function limitTone(left: number): { color: string; border: string; background: string; spent: boolean } {
  if (left <= 0) {
    return { color: "var(--text-on-accent)", border: "var(--critical)", background: "var(--critical)", spent: true };
  }
  if (left <= 15) {
    return { color: "var(--critical)", border: "var(--critical)", background: "color-mix(in srgb, var(--critical) 14%, transparent)", spent: false };
  }
  if (left <= 40) {
    return { color: "var(--warning)", border: "var(--warning)", background: "color-mix(in srgb, var(--warning) 12%, transparent)", spent: false };
  }
  return { color: "var(--positive)", border: "color-mix(in srgb, var(--positive) 45%, transparent)", background: "color-mix(in srgb, var(--positive) 10%, transparent)", spent: false };
}

/** Čerstvý údaj o vyčerpaném limitu — stroj stojí, i když hlásí ONLINE. */
/**
 * Co na stroji běží. Verze runneru je tu proto, aby šlo poznat, že se jeden
 * stroj po vydání nové verze neaktualizoval — instalace není automatická, jen
 * se nabídne. Verze CLI vysvětluje rozdíly v chování mezi stroji.
 */
function RunnerLine({ runner }: { runner: WorkerDTO["runner"] }) {
  if (!runner) return null;
  const parts = [
    runner.version ? `v${runner.version}` : null,
    runner.platform,
    runner.claudeVersion ? cs.workers.claudeVersion(runner.claudeVersion) : null,
  ].filter(Boolean);
  if (!parts.length) return null;
  return (
    <span style={{ color: "var(--text-secondary)" }}>
      {parts.join(" · ")}
      {" · "}
    </span>
  );
}

function isLimitBlocked(rateLimit: WorkerDTO["rateLimit"]): boolean {
  if (!rateLimit) return false;
  if (Date.now() - new Date(rateLimit.reportedAt).getTime() > RATE_LIMIT_STALE_MS) return false;
  const left = [rateLimit.sessionPct, rateLimit.weekPct, rateLimit.weekModelPct]
    .map(remaining)
    .filter((v): v is number => v != null);
  return rateLimit.status === "rejected" || left.some((v) => v === 0);
}

function RateLimitLine({ rateLimit }: { rateLimit: NonNullable<WorkerDTO["rateLimit"]> }) {
  const age = Date.now() - new Date(rateLimit.reportedAt).getTime();
  if (age > RATE_LIMIT_STALE_MS) return null;

  const windows = [
    { label: cs.workers.limitWindow5h, left: remaining(rateLimit.sessionPct) },
    { label: cs.workers.limitWindowWeek, left: remaining(rateLimit.weekPct) },
    { label: rateLimit.weekModelName, left: remaining(rateLimit.weekModelPct) },
  ].filter((w): w is { label: string; left: number } => !!w.label && w.left != null);

  const tightest = windows.length ? Math.min(...windows.map((w) => w.left)) : null;
  const blocked = isLimitBlocked(rateLimit);
  const statusColor = blocked
    ? "var(--critical)"
    : rateLimit.status === "allowed_warning" || (tightest != null && tightest <= 15)
      ? "var(--warning)"
      : "var(--text-secondary)";

  const label = blocked
    ? cs.workers.limitReached
    : rateLimit.status === "allowed_warning"
      ? cs.workers.limitWarning
      : cs.workers.limitOk;
  const type = rateLimit.type ? (cs.workers.limitTypes[rateLimit.type] ?? rateLimit.type) : null;

  return (
    <div className="mb-2.5">
      {windows.length ? (
        <div className="mb-1 flex flex-wrap items-center gap-1" data-testid="worker-limit-pct">
          <span className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {cs.workers.limitRemaining}
          </span>
          {windows.map((w) => {
            const tone = limitTone(w.left);
            return (
              <span
                key={w.label}
                className={`px-1.5 py-0.5 text-[12px] font-semibold tabular-nums ${tone.spent ? "af-animate-alert" : ""}`}
                style={{ borderRadius: "var(--radius-box)", color: tone.color, border: `1px solid ${tone.border}`, background: tone.background }}
              >
                {w.label} {w.left} %
              </span>
            );
          })}
        </div>
      ) : null}
      <div className={`text-[11px] ${blocked ? "font-semibold" : ""}`} style={{ color: statusColor }}>
        {type ? `${type} · ` : ""}
        {label}
        {rateLimit.resetsAt ? ` · ${cs.workers.limitResets(untilTime(rateLimit.resetsAt))}` : ""}
        {rateLimit.overage ? ` · ${cs.workers.limitOverage}` : ""}
      </div>
    </div>
  );
}


const statusTone: Record<string, Tone> = {
  ACTIVE: "green",
  COMPLETED: "green",
  PAUSED: "amber",
  FAILED: "red",
  INTERRUPTED: "red",
};

/** Workers (spec §40, /Design): registry, sessions, model analytics. */
export function WorkersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const workers = useQuery({ queryKey: ["workers"], queryFn: api.workers, refetchInterval: 30_000 });
  const sessions = useQuery({ queryKey: ["sessions"], queryFn: () => api.sessions({ limit: 25 }) });
  const analytics = useQuery({ queryKey: ["model-analytics"], queryFn: api.modelAnalytics });
  const [showResolved, setShowResolved] = useState(false);
  const blockers = useQuery({
    queryKey: ["worker-blockers", showResolved],
    queryFn: () => api.workerBlockers({ limit: 25, includeResolved: showResolved }),
  });
  const [connecting, setConnecting] = useState(false);
  useRealtimeEvent((m) => {
    if (m.type === "registry.changed" && m.area === "workers") {
      void queryClient.invalidateQueries({ queryKey: ["workers"] });
    }
    if (m.type === "project.section.changed" && m.section === "sessions") {
      void queryClient.invalidateQueries({ queryKey: ["sessions"] });
      void queryClient.invalidateQueries({ queryKey: ["workers"] });
      void queryClient.invalidateQueries({ queryKey: ["worker-blockers"] });
    }
  });

  const isOwner = session?.user.role === "OWNER";
  const list = workers.data?.workers ?? [];


  const sessionColumns: Column<WorkSessionSummaryDTO>[] = [
    {
      key: "id",
      label: cs.workers.session,
      width: "70px",
      cell: (s) => <span style={{ color: "var(--text-tertiary)" }}>#{s.id.slice(-5)}</span>,
    },
    {
      key: "worker",
      label: cs.workers.worker,
      width: "minmax(110px, 0.8fr)",
      primary: true,
      cell: (s) => s.workerName,
    },
    {
      key: "task",
      label: cs.workers.taskCol,
      width: "minmax(200px, 1.4fr)",
      cell: (s) => (
        <span style={{ color: "var(--text-secondary)" }}>
          {s.projectName} → #{s.taskNumber} {s.taskTitle}
        </span>
      ),
    },
    {
      key: "dept",
      label: cs.workers.dept,
      width: "100px",
      cell: (s) => <span style={{ color: "var(--accent)" }}>{s.departmentName ?? "—"}</span>,
    },
    {
      key: "model",
      label: cs.workers.model,
      width: "120px",
      cell: (s) => s.models[0] ?? "—",
    },
    {
      key: "cost",
      label: cs.workers.weekCost,
      width: "120px",
      // pod cenou i kroky: platí se za ně, takže patří do stejné buňky
      cell: (s) => (
        <>
          <span data-testid="session-week-cost">
            {s.weekPctSpent != null
              ? `${s.weekPctSpent.toFixed(1).replace(".", ",")} %`
              : s.tokensTotal != null
                ? `${Math.round(s.tokensTotal / 1000)}k`
                : "—"}
          </span>
          {sessionStepsLabel(s.turns, s.avgContextTokens) ? (
            <span className="block text-[11px]" style={{ color: "var(--text-tertiary)" }}>
              {sessionStepsLabel(s.turns, s.avgContextTokens)}
            </span>
          ) : null}
        </>
      ),
    },
    {
      key: "state",
      label: cs.workers.state,
      width: "110px",
      cell: (s) => (
        <span style={{ color: toneColor[statusTone[s.status] ?? "dim"] }}>
          {s.status === "ACTIVE" ? `● ${s.progress} %` : s.status}
        </span>
      ),
    },
  ];

  return (
    <div className="af-animate-fade">
      <PageHeader
        title={cs.workers.title}
        kicker="STROJE"
        lead={cs.workers.subtitle}
        right={
          <>
            <Btn onClick={() => void navigate({ to: "/workers/compare" })} data-testid="worker-compare">
              {cs.workers.compare}
            </Btn>
            {isOwner ? (
              <Btn variant="primary" onClick={() => setConnecting(true)} data-testid="worker-connect">
                {cs.workers.connect}
              </Btn>
            ) : null}
          </>
        }
      />

      {connecting ? <ConnectWorkerForm onClose={() => setConnecting(false)} /> : null}

      {/* worker karty */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {list.map((w) => (
          <WorkerCard key={w.id} worker={w} isOwner={isOwner} />
        ))}
        {list.length === 0 ? (
          <Panel className="col-span-full p-8">
            <EmptyState title={cs.workers.noWorkers} note={cs.workers.connectorDocs} />
          </Panel>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.6fr_1fr]">
        {/* sessions tabulka */}
        <Panel className="p-4">
          <div className="af-label mb-3" style={{ color: "var(--accent)" }}>
            {cs.workers.sessionsToday}
          </div>
          <DataTable
            columns={sessionColumns}
            rows={sessions.data?.sessions ?? []}
            rowKey={(s) => s.id}
            rowTestId={() => "session-row"}
            onRowClick={(s) => void navigate({ to: "/sessions/$id", params: { id: s.id } })}
            stackBelow={1024}
          />
          {(sessions.data?.sessions ?? []).length === 0 ? (
            <EmptyState title={cs.workers.noSessions} />
          ) : null}
        </Panel>

        <div className="flex flex-col gap-4">
          {/* blokace hlášené workery — otevřené; vyřešené odbavuje worker sám */}
          <Panel tone="red" className="p-4" data-testid="worker-blockers">
            <div className="flex items-baseline justify-between">
              <div className="af-label" style={{ color: "var(--critical)" }}>
                {cs.workers.blockers}
              </div>
              <button
                type="button"
                onClick={() => setShowResolved((v) => !v)}
                className="text-[11px]"
                style={{ color: "var(--text-tertiary)" }}
                data-testid="worker-blockers-toggle-resolved"
              >
                {showResolved ? cs.workers.hideResolvedBlockers : cs.workers.showResolvedBlockers}
              </button>
            </div>
            <div className="mb-3 mt-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
              {cs.workers.blockersNote}{" "}
              {/* Odbavení a kontext žijí na vlastní obrazovce — tady je jen náhled. */}
              <Link to="/blockers" style={{ color: "var(--accent)" }}>
                {cs.workers.blockersOpenScreen}
              </Link>
            </div>
            {(blockers.data?.blockers ?? []).map((b) => (
              <Link
                key={b.id}
                to="/blockers"
                className="mb-2 block px-3 py-2.5"
                style={{
                  border: b.resolvedAt ? "1px solid color-mix(in srgb, var(--positive) 15%, transparent)" : "1px solid color-mix(in srgb, var(--critical) 15%, transparent)",
                  opacity: b.resolvedAt ? 0.65 : 1,
                }}
                data-testid="worker-blocker-row"
              >
                <div className="flex justify-between text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                  <span>
                    {b.workerName} · {b.projectName} → #{b.taskNumber} {b.taskTitle}
                  </span>
                  <span>{relativeTime(b.createdAt)}</span>
                </div>
                <div className="mt-1 text-[12px] leading-relaxed" style={{ color: "var(--text-primary)" }}>
                  {b.resolvedAt ? "✓" : "⚠"} {b.text}
                </div>
                {b.resolvedAt ? (
                  <div className="mt-1 text-[11px] leading-relaxed" style={{ color: "var(--positive)" }}>
                    {cs.workers.blockerResolved(b.resolvedBy ?? "", relativeTime(b.resolvedAt))}
                    {b.resolutionNote ? ` — ${b.resolutionNote}` : ""}
                  </div>
                ) : null}
              </Link>
            ))}
            {(blockers.data?.blockers ?? []).length === 0 ? (
              <div className="text-[11px]" style={{ color: "var(--positive)" }}>
                {showResolved ? cs.workers.noBlockersEver : cs.workers.noBlockers}
              </div>
            ) : null}
          </Panel>

          {/* model analytics */}
          <Panel className="p-4">
            <div className="af-label" style={{ color: "var(--accent)" }}>
              {cs.workers.modelAnalytics}
            </div>
            <div className="mb-3 mt-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
              {cs.workers.modelNote}
            </div>
            {(analytics.data?.models ?? []).map((m) => (
              <div key={m.model} className="mb-2 px-3 py-2.5" style={{ borderRadius: "var(--radius-box)", border: "1px solid color-mix(in srgb, var(--accent) 8%, transparent)" }}>
                <div className="flex justify-between text-[12px]">
                  <span className="font-bold" style={{ color: "var(--text-primary)" }}>
                    {m.model}
                  </span>
                  <span style={{ color: "var(--positive)" }}>
                    {m.sessions > 0 ? `${Math.round((m.completed / m.sessions) * 100)} % completed` : "—"}
                  </span>
                </div>
                <div className="mt-1 text-[11px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                  {m.sessions} sessions · {Math.round(m.tokensTotal / 1000)}k tokens
                  {m.failed ? ` · ${m.failed} failed` : ""}
                </div>
              </div>
            ))}
            {(analytics.data?.models ?? []).length === 0 ? (
              <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                Data se objeví po prvních dokončených sessions.
              </div>
            ) : null}
          </Panel>
        </div>
      </div>

      {/* routines (spec §38) */}
      <RoutinesPanel isOwner={isOwner} />
    </div>
  );
}

function WorkerCard({ worker, isOwner }: { worker: WorkerDTO; isOwner: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [freshToken, setFreshToken] = useState<string | null>(null);
  const [editingProjects, setEditingProjects] = useState(false);
  const [editingBilling, setEditingBilling] = useState(false);
  const reissueMutation = useMutation({
    mutationFn: () => api.reissueWorkerToken(worker.id),
    onSuccess: (data) => setFreshToken(data.token),
  });
  const toggleMutation = useMutation({
    mutationFn: () => api.updateWorker(worker.id, { enabled: !worker.enabled }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["workers"] }),
  });

  const s = worker.activeSession;
  // vyčerpaný limit přebíjí zelený rámeček online stroje — dokud limit trvá,
  // stroj sice běží, ale práci nevezme
  const blocked = isLimitBlocked(worker.rateLimit);

  return (
    <Panel
      className="p-4"
      style={{
        borderColor: blocked
          ? "var(--critical)"
          : worker.online
            ? "color-mix(in srgb, var(--positive) 35%, transparent)"
            : "var(--border-default)",
        opacity: worker.enabled ? 1 : 0.5,
      }}
      data-testid={`worker-card-${worker.name}`}
    >
      <div className="mb-1.5 flex items-center justify-between">
        <button
          type="button"
          className="af-display cursor-pointer text-left text-[13px] font-semibold"
          style={{ color: "var(--text-primary)" }}
          onClick={() => void navigate({ to: "/workers/$id", params: { id: worker.id } })}
          data-testid={`worker-open-${worker.name}`}
        >
          {worker.name}
        </button>
        <span
          className={worker.online ? "af-animate-pulse text-[11px]" : "text-[11px]"}
          style={{ color: worker.online ? "var(--positive)" : "var(--text-tertiary)" }}
        >
          ● {!worker.enabled ? cs.workers.disabled : worker.online ? cs.workers.online : cs.workers.offline}
        </span>
      </div>
      <div className="mb-2 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
        <RunnerLine runner={worker.runner} />
        {worker.lastSeenAt ? cs.workers.lastSeen(relativeTime(worker.lastSeenAt)) : cs.workers.neverSeen}
        {worker.departmentNames.length ? ` · ${worker.departmentNames.join(", ")}` : ""}
        {worker.projectNames.length ? (
          <span style={{ color: "var(--warning)" }}>
            {" · "}
            {cs.workers.projectsOnly(worker.projectNames.join(", "))}
          </span>
        ) : (
          ""
        )}
      </div>
      {worker.rateLimit ? <RateLimitLine rateLimit={worker.rateLimit} /> : null}
      <div className="mb-2.5 flex flex-wrap gap-1">
        {worker.capabilities.map((c) => (
          <span key={c} className="px-1.5 py-0.5 text-[11px] tracking-[0.08em]" style={{ borderRadius: "var(--radius-box)", color: "var(--text-secondary)", border: "1px solid color-mix(in srgb, var(--accent) 15%, transparent)" }}>
            {c}
          </span>
        ))}
      </div>
      <div className="pt-2 text-[11px] leading-relaxed" style={{ borderTop: "1px solid color-mix(in srgb, var(--accent) 8%, transparent)", color: "var(--text-secondary)" }}>
        {s ? (
          <button
            type="button"
            className="cursor-pointer text-left"
            onClick={() => void navigate({ to: "/sessions/$id", params: { id: s.id } })}
          >
            <span style={{ color: "var(--accent)" }}>
              {s.projectName} → #{s.taskNumber} {s.taskTitle}
            </span>
            <br />
            <span style={{ color: "var(--positive)" }}>{s.progress} %</span>{" "}
            {s.currentActivity || "pracuje"} · {relativeTime(s.startedAt)}
            {sessionCostLabel(s.weekPctSpent, s.tokensTotal)
              ? ` · ${sessionCostLabel(s.weekPctSpent, s.tokensTotal)}`
              : ""}
            {sessionStepsLabel(s.turns, s.avgContextTokens)
              ? ` · ${sessionStepsLabel(s.turns, s.avgContextTokens)}`
              : ""}
          </button>
        ) : (
          cs.workers.idle
        )}
      </div>
      {isOwner ? (
        <div className="mt-2.5 flex items-center gap-2">
          <Btn className="!px-2 !py-1 text-[11px]" onClick={() => reissueMutation.mutate()}>
            {cs.workers.reissue}
          </Btn>
          <Btn
            className="!px-2 !py-1 text-[11px]"
            onClick={() => setEditingProjects((v) => !v)}
            data-testid={`worker-projects-edit-${worker.name}`}
          >
            {cs.workers.editProjects}
          </Btn>
          <Btn
            className="!px-2 !py-1 text-[11px]"
            tone={worker.billing?.monthlyPrice == null ? "amber" : "cyan"}
            variant={worker.billing?.monthlyPrice == null ? "secondary" : "ghost"}
            onClick={() => setEditingBilling((v) => !v)}
            data-testid={`worker-billing-edit-${worker.name}`}
          >
            {cs.workers.editBilling}
          </Btn>
          <button
            type="button"
            className="cursor-pointer text-[11px]"
            style={{ color: worker.enabled ? "var(--critical)" : "var(--positive)" }}
            onClick={() => toggleMutation.mutate()}
          >
            {worker.enabled ? "DEAKTIVOVAT" : "AKTIVOVAT"}
          </button>
        </div>
      ) : null}
      {editingProjects ? (
        <WorkerProjectsEditor worker={worker} onClose={() => setEditingProjects(false)} />
      ) : null}
      {editingBilling ? (
        <WorkerBillingEditor worker={worker} onClose={() => setEditingBilling(false)} />
      ) : null}
      {freshToken ? <TokenReveal token={freshToken} onClose={() => setFreshToken(null)} /> : null}
    </Panel>
  );
}

/** Toggle chips pro výběr projektů (prázdný výběr = worker smí všechny). */
function ProjectChips({
  selected,
  onToggle,
}: {
  selected: string[];
  onToggle: (id: string) => void;
}) {
  const projects = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  return (
    <div className="flex flex-wrap gap-1.5">
      {(projects.data?.projects ?? []).map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onToggle(p.id)}
          className="cursor-pointer px-2 py-1 text-[11px]"
          style={{ borderRadius: "var(--radius-box)",
            border: `1px solid ${selected.includes(p.id) ? "color-mix(in srgb, var(--accent) 50%, transparent)" : "var(--border-default)"}`,
            color: selected.includes(p.id) ? "var(--accent)" : "var(--text-secondary)",
          }}
          data-testid={`worker-project-chip-${p.slug}`}
        >
          {p.name}
        </button>
      ))}
    </div>
  );
}

/** Owner edituje povolené projekty existujícího workera přímo na kartě. */
function WorkerProjectsEditor({ worker, onClose }: { worker: WorkerDTO; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [projectIds, setProjectIds] = useState<string[]>(worker.projectIds);
  const mutation = useMutation({
    mutationFn: () => api.updateWorker(worker.id, { projectIds }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["workers"] });
      onClose();
    },
  });
  return (
    <div className="mt-2.5 pt-2.5" style={{ borderTop: "1px solid color-mix(in srgb, var(--accent) 8%, transparent)" }}>
      <div className="af-label-dim mb-1.5">{cs.workers.projects}</div>
      <ProjectChips
        selected={projectIds}
        onToggle={(id) =>
          setProjectIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
        }
      />
      <div className="mt-2 flex justify-end gap-2">
        <Btn className="!px-2 !py-1 text-[11px]" onClick={onClose}>
          {cs.common.cancel}
        </Btn>
        <Btn
          variant="primary"
          className="!px-2 !py-1 text-[11px]"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
          data-testid="worker-projects-save"
        >
          {cs.common.save}
        </Btn>
      </div>
    </div>
  );
}

/**
 * Předplatné stroje (p8a). Vlastní komponenta, protože se vyplňuje na dvou
 * místech: při připojení workera a dodatečně na jeho kartě — bez druhého
 * místa se u dřív připojených strojů nedaly koruny doplnit vůbec.
 */
function BillingFields({
  account,
  tariff,
  price,
  currency,
  onAccount,
  onTariff,
  onPrice,
  onCurrency,
}: {
  account: string;
  tariff: string;
  price: string;
  currency: string;
  onAccount: (v: string) => void;
  onTariff: (v: string) => void;
  onPrice: (v: string) => void;
  onCurrency: (v: string) => void;
}) {
  return (
    <>
      <div className="af-label-dim mb-2">{cs.workers.billing}</div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Field label={cs.workers.billingAccount}>
          <input
            className={inputClass}
            value={account}
            onChange={(e) => onAccount(e.target.value)}
            placeholder="claude-max-01"
            data-testid="worker-billing-account"
          />
        </Field>
        <Field label={cs.workers.billingTariff}>
          <input
            className={inputClass}
            value={tariff}
            onChange={(e) => onTariff(e.target.value)}
            placeholder="Max 20×"
            data-testid="worker-billing-tariff"
          />
        </Field>
        <Field label={cs.workers.billingPrice}>
          <input
            className={inputClass}
            value={price}
            onChange={(e) => onPrice(e.target.value)}
            placeholder="4990"
            inputMode="decimal"
            data-testid="worker-billing-price"
          />
        </Field>
        <Field label={cs.workers.billingCurrency}>
          <input
            className={inputClass}
            value={currency}
            onChange={(e) => onCurrency(e.target.value)}
            data-testid="worker-billing-currency"
          />
        </Field>
      </div>
      <div className="mt-2 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
        {price.trim()
          ? cs.workers.billingHint(
              (Number(price.replace(",", ".")) / 4.33 / 100).toFixed(2).replace(".", ","),
              currency,
            )
          : cs.workers.billingEmpty}
      </div>
    </>
  );
}

/** Owner doplní nebo změní předplatné existujícího stroje přímo na kartě. */
function WorkerBillingEditor({ worker, onClose }: { worker: WorkerDTO; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [account, setAccount] = useState(worker.billing?.account ?? "");
  const [tariff, setTariff] = useState(worker.billing?.tariff ?? "");
  const [price, setPrice] = useState(
    worker.billing?.monthlyPrice != null ? String(worker.billing.monthlyPrice) : "",
  );
  const [currency, setCurrency] = useState(worker.billing?.currency ?? "Kč");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.updateWorker(worker.id, {
        // prázdné pole se posílá taky — jinak by šlo údaj jen přidat, ne smazat
        billingAccount: account.trim(),
        billingTariff: tariff.trim(),
        billingPrice: price.trim() ? Number(price.replace(",", ".")) : 0,
        billingCurrency: currency.trim() || "Kč",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["workers"] });
      void queryClient.invalidateQueries({ queryKey: ["economy"] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });

  return (
    <div
      className="mt-2.5 pt-2.5"
      style={{ borderTop: "1px solid color-mix(in srgb, var(--accent) 8%, transparent)" }}
      data-testid="worker-billing-editor"
    >
      <BillingFields
        account={account}
        tariff={tariff}
        price={price}
        currency={currency}
        onAccount={setAccount}
        onTariff={setTariff}
        onPrice={setPrice}
        onCurrency={setCurrency}
      />
      {error ? (
        <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
          ⚠ {error}
        </div>
      ) : null}
      <div className="mt-2 flex justify-end gap-2">
        <Btn className="!px-2 !py-1 text-[11px]" onClick={onClose}>
          {cs.common.cancel}
        </Btn>
        <Btn
          variant="primary"
          className="!px-2 !py-1 text-[11px]"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
          data-testid="worker-billing-save"
        >
          {cs.common.save}
        </Btn>
      </div>
    </div>
  );
}

function ConnectWorkerForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const departments = useQuery({ queryKey: ["departments"], queryFn: api.departments });
  const [name, setName] = useState("");
  const [capabilities, setCapabilities] = useState("code, git, filesystem, browser");
  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  // předplatné stroje (§7.2) — bez něj se spotřeba ukáže jen v procentech
  const [billingAccount, setBillingAccount] = useState("");
  const [billingTariff, setBillingTariff] = useState("");
  const [billingPrice, setBillingPrice] = useState("");
  const [billingCurrency, setBillingCurrency] = useState("Kč");
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.createWorker({
        name,
        capabilities: capabilities.split(",").map((s) => s.trim()).filter(Boolean),
        departmentIds,
        projectIds,
        ...(billingAccount.trim() ? { billingAccount: billingAccount.trim() } : {}),
        ...(billingTariff.trim() ? { billingTariff: billingTariff.trim() } : {}),
        ...(billingPrice.trim() ? { billingPrice: Number(billingPrice.replace(",", ".")) } : {}),
        ...(billingCurrency.trim() ? { billingCurrency: billingCurrency.trim() } : {}),
      }),
    onSuccess: (data) => {
      setToken(data.token);
      void queryClient.invalidateQueries({ queryKey: ["workers"] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });

  return (
    <Panel brackets className="mb-4 max-w-2xl p-4" data-testid="worker-form">
      {token ? (
        <TokenReveal
          token={token}
          onClose={() => {
            setToken(null);
            onClose();
          }}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={cs.workers.name}>
              <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Mac Studio Home" autoFocus data-testid="worker-name" />
            </Field>
            <Field label={cs.workers.capabilities}>
              <input className={inputClass} value={capabilities} onChange={(e) => setCapabilities(e.target.value)} />
            </Field>
          </div>
          {/* účet a tarif (p8a) — z nich se počítá cena spotřeby */}
          <div
            className="mt-3 p-3.5"
            style={{
              borderRadius: "var(--radius-box)",
              border: "1px solid var(--border-default)",
              background: "var(--surface-inset)",
            }}
          >
            <BillingFields
              account={billingAccount}
              tariff={billingTariff}
              price={billingPrice}
              currency={billingCurrency}
              onAccount={setBillingAccount}
              onTariff={setBillingTariff}
              onPrice={setBillingPrice}
              onCurrency={setBillingCurrency}
            />
          </div>

          <div className="mt-3">
            <div className="af-label-dim mb-1.5">{cs.workers.departments}</div>
            <div className="flex flex-wrap gap-1.5">
              {(departments.data?.departments ?? [])
                .filter((d) => d.enabled)
                .map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() =>
                      setDepartmentIds((prev) =>
                        prev.includes(d.id) ? prev.filter((x) => x !== d.id) : [...prev, d.id],
                      )
                    }
                    className="cursor-pointer px-2 py-1 text-[11px]"
                    style={{ borderRadius: "var(--radius-box)",
                      border: `1px solid ${departmentIds.includes(d.id) ? "color-mix(in srgb, var(--accent) 50%, transparent)" : "var(--border-default)"}`,
                      color: departmentIds.includes(d.id) ? "var(--accent)" : "var(--text-secondary)",
                    }}
                  >
                    {d.name}
                  </button>
                ))}
            </div>
          </div>
          <div className="mt-3">
            <div className="af-label-dim mb-1.5">{cs.workers.projects}</div>
            <ProjectChips
              selected={projectIds}
              onToggle={(id) =>
                setProjectIds((prev) =>
                  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                )
              }
            />
          </div>
          {error ? (
            <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
              ⚠ {error}
            </div>
          ) : null}
          <div className="mt-3 flex justify-end gap-2">
            <Btn onClick={onClose}>{cs.common.cancel}</Btn>
            <Btn variant="primary" disabled={name.trim().length < 2 || mutation.isPending} onClick={() => mutation.mutate()} data-testid="worker-save">
              {cs.common.create}
            </Btn>
          </div>
        </>
      )}
    </Panel>
  );
}

function TokenReveal({ token, onClose }: { token: string; onClose: () => void }) {
  return (
    <div className="mt-2" data-testid="worker-token">
      <div className="af-label" style={{ color: "var(--warning)" }}>
        {cs.workers.tokenTitle}
      </div>
      <div
        className="mt-2 select-all break-all px-3 py-2.5 font-mono text-[11px]"
        style={{ borderRadius: "var(--radius-box)", border: "1px solid color-mix(in srgb, var(--warning) 40%, transparent)", background: "color-mix(in srgb, var(--warning) 4%, transparent)", color: "var(--text-primary)" }}
        data-testid="worker-token-value"
      >
        {token}
      </div>
      <div className="mt-1.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
        {cs.workers.tokenNote} · {cs.workers.connectorDocs}
      </div>
      <div className="af-label mt-3" style={{ color: "var(--accent)" }}>
        {cs.workers.bootstrapTitle}
      </div>
      <pre
        className="mt-2 select-all whitespace-pre-wrap break-words px-3 py-2.5 font-mono text-[11px] leading-relaxed"
        style={{ borderRadius: "var(--radius-box)", border: "1px solid color-mix(in srgb, var(--accent) 25%, transparent)", background: "color-mix(in srgb, var(--accent) 3%, transparent)", color: "var(--text-primary)" }}
        data-testid="worker-bootstrap-prompt"
      >
        {cs.workers.bootstrapPrompt(window.location.origin, token)}
      </pre>
      <div className="mt-2.5 flex justify-end">
        <Btn onClick={onClose}>{cs.common.close}</Btn>
      </div>
    </div>
  );
}
